// @flow
import { pickBy } from 'lodash';

import RemindAPI from 'lib/RemindAPI';
import GET_AUTO_MESSAGES_FOR_ORG_QUERY from 'admin/components/AdminAutoMessages/queries/GetAutoMessagesForOrg.gql';
import PUT_SAMPLE_MESSAGE_MUTATION from 'admin/components/AdminNewAutoMessage/queries/PutSampleMessageMutation.gql';
import SAVE_AUTO_MESSAGE_MUTATION from 'admin/components/AdminNewAutoMessage/queries/SaveAutoMessageMutation.gql';
import SET_AUTO_MESSAGE_STATUS_MUTATION from 'admin/components/AdminNewAutoMessage/queries/SetAutoMessageStatusMutation.gql';
import DELETE_AUTO_MESSAGE_MUTATION from 'admin/components/AdminNewAutoMessage/queries/DeleteAutoMessageMutation.gql';
import ToastActions from 'actions/ToastActions';

import type { errorFragment, AutoMessageStatus } from 'queries/__generated__/errorFragment';

export function handleGQLError(error: errorFragment, consoleErrorText: string) {
  console.warn(error);
  console.error(`AutoMessaging - ${consoleErrorText}`);
  ToastActions.showGQLError(error);
}

export function setAutoMessageStatus(
  messageUuid: string,
  status: AutoMessageStatus,
  viewingOrgUuid: string,
  messageName: string,
  onChange?: () => void
) {
  const statusText = status === 'active' ? 'activated' : 'paused';
  RemindAPI.execute({
    mutation: SET_AUTO_MESSAGE_STATUS_MUTATION,
    variables: {
      input: {
        uuid: messageUuid,
        status,
      },
    },
    refetchQueries: [
      {
        query: GET_AUTO_MESSAGES_FOR_ORG_QUERY,
        variables: { organizationUUID: viewingOrgUuid },
      },
    ],
  }).then(
    res => {
      const { error } = res.setAutoMessageStatus;
      if (error) {
        handleGQLError(error, `AutoMessageActions - message unsuccessfully ${statusText}`);
        return;
      }
      ToastActions.showSuccessToast(`${messageName} successfully ${statusText}`);
      if (onChange) {
        onChange();
      }
    },
    err => {
      console.warn(err);
      console.error(`AutoMessageActions - message unsuccessfully ${statusText}`);
      ToastActions.showErrorToast('Oops, looks like something went wrong');
    }
  );
}

export function duplicateAutoMessage(
  fieldsToDuplicate: *,
  viewingOrgUuid: string,
  redirect?: (uuid: string) => void
) {
  const messageContent = fieldsToDuplicate.messageContent && {
    body: fieldsToDuplicate.messageContent.body,
  };

  const sender = fieldsToDuplicate.sender && {
    uuid: fieldsToDuplicate.sender.uuid,
    type: fieldsToDuplicate.sender.__typename === 'EntitySender' ? 'district' : 'user',
  };

  const fullInput = {
    messageName: `Copy of ${fieldsToDuplicate.messageName}`,
    orgUuid: fieldsToDuplicate.organization.uuid,
    creatorOrgUuid: viewingOrgUuid,
    includeParents: fieldsToDuplicate.includeParents,
    includeStudents: fieldsToDuplicate.includeStudents,
    messageContent,
    sender,
    customMessageFields: fieldsToDuplicate.customMessageFields,
  };
  const filteredInput = pickBy(fullInput, field => !(field == null));

  if (!filteredInput.orgUuid) {
    console.error('AutoMessageActions - tried to duplicate an auto message without an orgUuid');
    ToastActions.showErrorToast('Oops, looks like something went wrong');
    return;
  }

  RemindAPI.execute({
    mutation: SAVE_AUTO_MESSAGE_MUTATION,
    variables: {
      input: filteredInput,
    },
    refetchQueries: [
      {
        query: GET_AUTO_MESSAGES_FOR_ORG_QUERY,
        variables: { organizationUUID: viewingOrgUuid },
      },
    ],
  }).then(
    res => {
      const { error } = res.saveAutoMessage;
      if (error) {
        handleGQLError(error, 'AutoMessageActions - error duplicating an auto message');
        return;
      }
      ToastActions.showSuccessToast(
        `${fieldsToDuplicate.messageName || 'Message'} successfully duplicated`
      );
      if (redirect) {
        const messageUuid = res.saveAutoMessage.autoMessage.uuid;
        redirect(messageUuid);
      }
    },
    err => {
      console.warn(err);
      console.error('AutoMessageActions - error duplicating an auto message');
      ToastActions.showErrorToast('Oops, looks like something went wrong');
    }
  );
}

export function deleteAutoMessage(
  messageUuid: string,
  messageName: string,
  viewingOrgUuid: string,
  redirect?: (uuid: string) => void,
  onDelete?: () => void
) {
  RemindAPI.execute({
    mutation: DELETE_AUTO_MESSAGE_MUTATION,
    variables: {
      input: { uuid: messageUuid },
    },
    refetchQueries: [
      {
        query: GET_AUTO_MESSAGES_FOR_ORG_QUERY,
        variables: { organizationUUID: viewingOrgUuid },
      },
    ],
  }).then(
    res => {
      const { error } = res.deleteAutoMessage;
      if (error) {
        handleGQLError(error, 'AutoMessageActions - error deleting an auto message');
        return;
      }
      ToastActions.showSuccessToast(`${messageName} successfully deleted`);
      if (redirect) {
        redirect(messageUuid);
      }
      if (onDelete) {
        onDelete();
      }
    },
    err => {
      console.warn(err);
      console.error('AutoMessageActions - error deleting an auto message');
      ToastActions.showErrorToast('Oops, looks like something went wrong');
    }
  );
}

export function sendTestMessage(
  messageBody: string,
  fileAttachmentUuids: string[],
  deviceUuid?: string
) {
  if (!deviceUuid) {
    console.error('AutoMessageActions - tried to send test message without deviceUuid');
    ToastActions.showErrorToast(
      'Oops, looks like you tried to send a test message without an enabled phone number'
    );
    return;
  }
  RemindAPI.execute({
    mutation: PUT_SAMPLE_MESSAGE_MUTATION,
    variables: {
      message: {
        body: messageBody,
        fileUuids: fileAttachmentUuids,
        linkPreviews: [],
        urgent: false,
      },
      smsDeviceUUID: deviceUuid,
    },
  }).then(
    res => {
      const { error } = res.putSampleMessage;
      if (error) {
        handleGQLError(error, 'AutoMessageActions - error sending a test message');
        return;
      }
      ToastActions.showSuccessToast('Test message successfully sent');
    },
    err => {
      console.warn(err);
      console.error('AutoMessageActions - error sending a test message');
      ToastActions.showErrorToast('Oops, looks like something went wrong');
    }
  );
}
